import {
  effectivePaymentSourceOf,
  prepaymentReviewNeeded,
  resolveEntry,
  settlementStatusOf,
  type Basis,
  type Priority,
  type SettlementStatus,
} from '@progear/finance-core';
import { PRIORITY_LABEL, SETTLEMENT_STATUS_LABEL, type CashFlowEntry } from './cashFlow';
import { SETTLEMENT_SOURCE_META_LIST } from './settlementSource';

/**
 * Faceted filter model for the cash-flow toolbar (module 1.1), rendered 1:1 with
 * the approved canvas. Every facet maps to a REAL field on the shared
 * `CashFlowEntry` (no demo derivations) so a filter genuinely narrows the stream;
 * the narrowed entries are re-rolled by finance-core (master rule: one truth per
 * number — the view never recomputes). Pure + deterministic so it is fully
 * unit-testable.
 *
 * Canvas facet order: Suund · Dokumendi liik · Tehingu liik · Kindlus · Makse
 * staatus · Allikas · Osapool · Projekt · Dokumendi koostaja · Kirje tüüp · Summa
 * vahemik. `Makse staatus`, `Vajab kontrolli` and `Arvelduse allikas` are the
 * computed facets (finance-core resolves their values — the reason the matching
 * helpers take `today`); every other facet reads a stored field.
 */
export type FilterKey =
  | 'direction'
  | 'docType'
  | 'txnType'
  | 'certainty'
  | 'priority'
  | 'settlementStatus'
  | 'review'
  | 'source'
  | 'settlementSource'
  | 'party'
  | 'project'
  | 'author'
  | 'category';

export const FILTER_KEYS: FilterKey[] = [
  'direction',
  'docType',
  'txnType',
  'certainty',
  'priority',
  'settlementStatus',
  'review',
  'source',
  'settlementSource',
  'party',
  'project',
  'author',
  'category',
];

export interface FacetOption {
  /** The raw entry value the option matches. */
  value: string;
  /** Estonian on-screen label. */
  label: string;
  /** Optional colour dot (income/cost) — the Kirje tüüp facet mirrors LINE_META. */
  dotKind?: 'income' | 'cost';
}

export interface FacetGroup {
  key: FilterKey;
  label: string;
  options: FacetOption[];
}

/** Suund — the entry's `kind`. */
export const DIRECTION_FACET: FacetGroup = {
  key: 'direction',
  label: 'Suund',
  options: [
    { value: 'income', label: 'Tulu' },
    { value: 'cost', label: 'Kulu' },
  ],
};

/** Dokumendi liik — the entry's `docType` (document metadata). */
export const DOCTYPE_FACET: FacetGroup = {
  key: 'docType',
  label: 'Dokumendi liik',
  options: [
    { value: 'invoice', label: 'Arve' },
    { value: 'prepayment', label: 'Ettemaks' },
    { value: 'credit_note', label: 'Kreedit' },
    { value: 'offer', label: 'Pakkumine' },
    { value: 'delivery_invoice', label: 'Saateleht' },
    { value: 'none', label: 'Dokumendita / Muu' },
  ],
};

/** Tehingu liik — the entry's `txnType` (document metadata). */
export const TXNTYPE_FACET: FacetGroup = {
  key: 'txnType',
  label: 'Tehingu liik',
  options: [
    { value: 'retail', label: 'Jaemüük' },
    { value: 'ecommerce', label: 'E-müük' },
    { value: 'project_sale', label: 'Projektimüük' },
    { value: 'wholesale', label: 'Hulgimüük' },
    { value: 'service', label: 'Teenus' },
    { value: 'rental', label: 'Rent' },
  ],
};

/** value→Estonian label maps for the document-metadata enums — the ONE place these
 *  are named, derived from the facet options so the cell-detail/ledger badges read
 *  identically to the filter (master rule: don't reinvent terminology). */
export const DOC_TYPE_LABEL: Record<string, string> = Object.fromEntries(
  DOCTYPE_FACET.options.map((o) => [o.value, o.label]),
);
export const TXN_TYPE_LABEL: Record<string, string> = Object.fromEntries(
  TXNTYPE_FACET.options.map((o) => [o.value, o.label]),
);

/** Kindlus — the entry's `certainty`. */
export const CERTAINTY_FACET: FacetGroup = {
  key: 'certainty',
  label: 'Kindlus',
  options: [
    { value: 'actual', label: 'Tegelik' },
    { value: 'committed', label: 'Kokkulepitud' },
    { value: 'forecast', label: 'Prognoos' },
  ],
};

/**
 * Prioriteet — the entry's manual `priority` flag (the ONE place these are named
 * is PRIORITY_LABEL in cashFlow.ts, derived from finance-core), so the facet, the
 * register badge and the calendar chip flag all read identically (master rule:
 * one truth per number, don't reinvent terminology). An entry with no priority
 * contributes '' (see entryFacetValue) and is never matched by this facet.
 */
export const PRIORITY_FACET: FacetGroup = {
  key: 'priority',
  label: 'Prioriteet',
  options: (['critical', 'important', 'focus'] as Priority[]).map((v) => ({
    value: v,
    label: PRIORITY_LABEL[v],
  })),
};

/**
 * Makse staatus — the entry's settlement status, the ONE place this is computed
 * is finance-core (`settlementStatusOf`); the options reuse SETTLEMENT_STATUS_LABEL
 * so the facet, the ledger badge and the cell-detail badge all read identically
 * (master rule: one truth per number, don't reinvent terminology).
 */
export const SETTLEMENT_STATUS_FACET: FacetGroup = {
  key: 'settlementStatus',
  label: 'Makse staatus',
  options: (['settled', 'overdue', 'partial', 'planned', 'pending'] as SettlementStatus[]).map((v) => ({
    value: v,
    label: SETTLEMENT_STATUS_LABEL[v],
  })),
};

/**
 * Vajab kontrolli — the register/calendar "kontrolli" marker as a facet, so the
 * flagged rows are findable in one click. The ONE place the decision is computed
 * is finance-core (`prepaymentReviewNeeded` — katteta ettemaks past its due day);
 * the facet only matches the SAME flag the marker renders (master rule: one
 * truth). Like `Makse staatus` it is a computed facet, so matching takes `today`.
 * An unflagged entry contributes '' (see entryFacetValue) and is never matched.
 */
export const REVIEW_FACET: FacetGroup = {
  key: 'review',
  label: 'Vajab kontrolli',
  options: [{ value: 'review_needed', label: 'Katteta ettemaks üle tähtaja' }],
};

/** Allikas — the entry's sync `source` (the canvas surfaces the four connectors). */
export const SOURCE_FACET: FacetGroup = {
  key: 'source',
  label: 'Allikas',
  options: [
    { value: 'erply', label: 'Erply' },
    { value: 'smartaccounts', label: 'SmartAccounts' },
    { value: 'pipedrive', label: 'Pipedrive' },
    { value: 'lhv', label: 'LHV' },
    { value: 'forecast', label: 'Prognoos' },
    { value: 'payment_plan', label: 'Maksekokkulepe' },
    { value: 'contract', label: 'Leping' },
  ],
};

/**
 * Arvelduse allikas — the entry's settlement TRUTH source, the ONE place this is
 * resolved is finance-core (`effectivePaymentSourceOf` — the highest-priority
 * source WITH cash); the options reuse SETTLEMENT_SOURCE_META_LIST so the facet,
 * the register `Allikas` column and the detail per-source overview all read
 * identically (master rule: one truth per number, don't reinvent terminology).
 * An unsettled entry contributes '' (see entryFacetValue) and is never matched.
 */
export const SETTLEMENT_SOURCE_FACET: FacetGroup = {
  key: 'settlementSource',
  label: 'Arvelduse allikas',
  options: SETTLEMENT_SOURCE_META_LIST.map((m) => ({ value: m.source, label: m.label })),
};

/**
 * Osapool — the counterparty ROLE, derived from `kind`: an income line faces a
 * customer (Klient), a cost line a supplier (Tarnija). Matches the canvas, which
 * groups the stream by role rather than by individual party name.
 */
export const PARTY_FACET: FacetGroup = {
  key: 'party',
  label: 'Osapool',
  options: [
    { value: 'income', label: 'Klient' },
    { value: 'cost', label: 'Tarnija' },
  ],
};

/**
 * Kirje tüüp — the entry's `category` (the 7 named lines). Labels + dot colour
 * mirror LINE_META (categoryMeta.ts) so the line is named identically everywhere;
 * the dot is a filter-UI cue (income green / cost red).
 */
export const CATEGORY_FACET: FacetGroup = {
  key: 'category',
  label: 'Kirje tüüp',
  options: [
    { value: 'sale', label: 'Müük', dotKind: 'income' },
    { value: 'direct_cost', label: 'Otsekulud', dotKind: 'cost' },
    { value: 'operating_cost', label: 'Tegevuskulud', dotKind: 'cost' },
    { value: 'payroll', label: 'Tööjõukulud', dotKind: 'cost' },
    { value: 'financial_cost', label: 'Finantskulud', dotKind: 'cost' },
    { value: 'tax_agreement', label: 'Maksud', dotKind: 'cost' },
    { value: 'loan', label: 'Laenud', dotKind: 'cost' },
  ],
};

/** The facets with fixed (enum-backed) option sets, in canvas order. Projekt and
 * Dokumendi koostaja are built per-window (see buildFacets). */
export const STATIC_FACETS: FacetGroup[] = [
  DIRECTION_FACET,
  DOCTYPE_FACET,
  TXNTYPE_FACET,
  CERTAINTY_FACET,
  PRIORITY_FACET,
  SETTLEMENT_STATUS_FACET,
  REVIEW_FACET,
  SOURCE_FACET,
  SETTLEMENT_SOURCE_FACET,
  PARTY_FACET,
];

/**
 * The "Täna" quick filter (calendar Täna button + its DUE/ISSUED sub-choice).
 * `none` = inactive; `due` keeps rows that LAND on today — finance-core's
 * `resolveEntry` effective date, so an unpaid overdue row carried forward to today
 * counts exactly as the calendar places it; `document` keeps rows whose issue date
 * (documentDate) is today. It lives in the
 * SHARED workspace filter so every lens (calendar/matrix/register) narrows
 * identically (L14) — never a lens-local "today" toggle.
 */
export type TodayMode = 'none' | 'due' | 'document';

export interface CashFlowFilters {
  sel: Record<FilterKey, Set<string>>;
  /** Inclusive gross amount floor in euros (free text; parsed lazily). */
  amountMin: string;
  /** Inclusive gross amount ceiling in euros. */
  amountMax: string;
  /**
   * Free-text search across the entry's documentary fields (see `entrySearchText`).
   * Lives in the SHARED workspace filter — the register search box drives THIS, never
   * a lens-local state — so a search narrows the calendar and matrix too (L14).
   */
  search: string;
  /** The shared "Täna" quick filter (see {@link TodayMode}). */
  todayMode: TodayMode;
}

export function emptyFilters(): CashFlowFilters {
  return {
    sel: {
      direction: new Set(),
      docType: new Set(),
      txnType: new Set(),
      certainty: new Set(),
      priority: new Set(),
      settlementStatus: new Set(),
      review: new Set(),
      source: new Set(),
      settlementSource: new Set(),
      party: new Set(),
      project: new Set(),
      author: new Set(),
      category: new Set(),
    },
    amountMin: '',
    amountMax: '',
    search: '',
    todayMode: 'none',
  };
}

/** How many distinct constraints are active (each amount bound + the search +
 *  the Täna quick filter each count once). */
export function filterCount(f: CashFlowFilters): number {
  let n = f.amountMin.trim() || f.amountMax.trim() ? 1 : 0;
  if (f.search.trim()) n += 1;
  if (f.todayMode !== 'none') n += 1;
  for (const key of FILTER_KEYS) n += f.sel[key].size;
  return n;
}

/**
 * The value an entry contributes to a given facet ('' when the field is null).
 * `today` only matters for `settlementStatus`, whose value is computed by
 * finance-core (every other facet reads a stored field).
 */
export function entryFacetValue(entry: CashFlowEntry, key: FilterKey, today: string): string {
  switch (key) {
    case 'direction':
      return entry.kind;
    case 'docType':
      return entry.docType ?? '';
    case 'txnType':
      return entry.txnType ?? '';
    case 'certainty':
      return entry.certainty;
    case 'priority':
      return entry.priority ?? '';
    case 'settlementStatus':
      return settlementStatusOf(entry, today);
    // Vajab kontrolli — finance-core's ONE review decision (the same flag the
    // register/calendar ⚠ marker renders); unflagged entries contribute ''.
    case 'review':
      return prepaymentReviewNeeded(entry, today) ? 'review_needed' : '';
    case 'source':
      return entry.source;
    // Arvelduse allikas — the settlement TRUTH source (finance-core resolves which
    // source wins); an unsettled entry contributes '' and is never matched.
    case 'settlementSource':
      return effectivePaymentSourceOf(entry)?.source ?? '';
    // Osapool is the counterparty ROLE, derived from the entry's direction.
    case 'party':
      return entry.kind;
    // Projekt prefers the document's project_name (project_name), falling back to
    // the legacy project code, so the facet keys on the human-readable name.
    case 'project':
      return entry.projectName ?? entry.project ?? '';
    case 'author':
      return entry.author ?? '';
    case 'category':
      return entry.category;
  }
}

/** Distinct, sorted (et-EE) non-null values of a nullable text field → a facet. */
function buildValueFacet(
  entries: CashFlowEntry[],
  key: 'project' | 'author',
  label: string,
  pick: (e: CashFlowEntry) => string | null,
): FacetGroup {
  const seen = new Set<string>();
  for (const e of entries) {
    const v = pick(e);
    if (v) seen.add(v);
  }
  const options = [...seen]
    .sort((a, b) => a.localeCompare(b, 'et-EE'))
    .map((v) => ({ value: v, label: v }));
  return { key, label, options };
}

/** Projekt — the distinct projects in the window, preferring the document's
 *  project_name over the legacy project code (kept in step with entryFacetValue). */
export function buildProjectFacet(entries: CashFlowEntry[]): FacetGroup {
  return buildValueFacet(entries, 'project', 'Projekt', (e) => e.projectName ?? e.project);
}

/** Dokumendi koostaja — the distinct authors present in the window. */
export function buildAuthorFacet(entries: CashFlowEntry[]): FacetGroup {
  return buildValueFacet(entries, 'author', 'Dokumendi koostaja', (e) => e.author);
}

/** The full, canvas-ordered facet list (static facets + dynamic Projekt/koostaja). */
export function buildFacets(entries: CashFlowEntry[]): FacetGroup[] {
  return [
    DIRECTION_FACET,
    DOCTYPE_FACET,
    TXNTYPE_FACET,
    CERTAINTY_FACET,
    PRIORITY_FACET,
    SETTLEMENT_STATUS_FACET,
    REVIEW_FACET,
    SOURCE_FACET,
    SETTLEMENT_SOURCE_FACET,
    PARTY_FACET,
    buildProjectFacet(entries),
    buildAuthorFacet(entries),
    CATEGORY_FACET,
  ];
}

/** Euros free-text → integer cents (filter threshold only — never a money calc). */
function eurosToCents(text: string): number | null {
  const v = parseFloat(text.replace(',', '.'));
  return Number.isFinite(v) ? Math.round(v * 100) : null;
}

/**
 * The lower-cased free-text haystack a `search` term is tested against — the entry's
 * documentary fields (document no. · offer · party · project · author · comment),
 * matching the register search box's advertised scope. Document no. falls back to the
 * description and then the id; project prefers the document `project_name`. Shared so
 * every lens searches identically (L14); reads stored fields only (never a money calc).
 */
export function entrySearchText(entry: CashFlowEntry): string {
  return [
    entry.docNumber ?? entry.description ?? entry.id,
    entry.offerNumber ?? '',
    entry.party,
    entry.projectName ?? entry.project ?? '',
    entry.author ?? '',
    entry.comment ?? '',
  ]
    .join(' ')
    .toLowerCase();
}

/**
 * Normalise a term + haystack to ONE shape so punctuation and spacing never block a
 * match: lower-cased, with every non-letter/digit run stripped. This makes the
 * register + Cmd+K search hyphen- and space-tolerant — `pr-466`, `PR 466` and
 * `pr466` all match the same document. Shared so every lens searches identically
 * (L14); reads stored text only (never a money calc).
 */
function normalizeSearchText(text: string): string {
  return text.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '');
}

/** AND across active facets (OR within a facet); gross amount within the range; the
 *  free-text `search` within the entry's document haystack; the Täna quick filter.
 *  `today` resolves the computed `settlementStatus` facet (ignored by the rest) AND
 *  the `due`/`document` today modes. `basis` re-places the DUE mode's effective date
 *  (accrual pins document dates, so nothing is carried to today there — L14 shared). */
export function entryMatchesFilters(
  entry: CashFlowEntry,
  f: CashFlowFilters,
  today: string,
  basis: Basis = 'cashBasis',
): boolean {
  for (const key of FILTER_KEYS) {
    const set = f.sel[key];
    if (set.size && !set.has(entryFacetValue(entry, key, today))) return false;
  }
  const min = eurosToCents(f.amountMin);
  const max = eurosToCents(f.amountMax);
  if (min !== null && entry.money.gross < min) return false;
  if (max !== null && entry.money.gross > max) return false;
  const term = normalizeSearchText(f.search);
  if (term && !normalizeSearchText(entrySearchText(entry)).includes(term)) return false;
  // Täna quick filter (carry-forward aware so it matches the calendar — L14): DUE
  // keeps rows that LAND on today (resolveEntry's effective date — an unpaid overdue
  // row carried forward to today counts, exactly as the calendar places it); ISSUED
  // keeps rows whose issue date (documentDate) is today.
  if (f.todayMode === 'due' && resolveEntry(entry, today, basis).effectiveDate !== today) return false;
  if (f.todayMode === 'document' && entry.documentDate !== today) return false;
  return true;
}

/** How many entries carry each value of a facet (independent per-option tally).
 *  `today` resolves the computed `settlementStatus` facet (ignored by the rest). */
export function facetCounts(entries: CashFlowEntry[], key: FilterKey, today: string): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const e of entries) {
    const v = entryFacetValue(e, key, today);
    counts[v] = (counts[v] ?? 0) + 1;
  }
  return counts;
}

export interface ActiveChip {
  id: string;
  key: FilterKey;
  value: string;
  label: string;
}

/** The removable chips for the active selection (amount handled separately). */
export function activeChips(f: CashFlowFilters, facets: FacetGroup[]): ActiveChip[] {
  const labelOf = (key: FilterKey, value: string): string => {
    const group = facets.find((g) => g.key === key);
    return group?.options.find((o) => o.value === value)?.label ?? value;
  };
  const chips: ActiveChip[] = [];
  for (const key of FILTER_KEYS) {
    for (const value of f.sel[key]) {
      chips.push({ id: `${key}:${value}`, key, value, label: labelOf(key, value) });
    }
  }
  return chips;
}
